import { useMemo } from 'react';
import { Button } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { ArrowRightIcon } from 'components/v2/icons/ArrowRightIcon';
import { CurrencyCode } from 'constants/enums/general';
import { useLocalization } from 'localization';
import { IProductDto } from 'api/products/dto';
import { CUSTOMIZE_PACKAGE_ID } from 'modules/v2/order/neworder/CreateOrderStep2';
import ProductsTooltip from 'components/v2/shared/ProductsTooltip';
import { userStore } from 'stores/userStore';

type Props = {
  handleChosePackageProduct: (packageItem?: IProductDto) => void;
};

export const useCreateNewOrderStep2 = (payload: Props) => {
  const { t, f } = useLocalization();
  const { handleChosePackageProduct } = payload;
  const { user } = userStore();

  const packagesColumns = useMemo((): GridColDef<IProductDto>[] => {
    const columns: GridColDef<IProductDto>[] = [
      {
        headerName: t('orders.pages.new.flowSelection.packageName'),
        field: 'name',
        minWidth: 500,
        sortable: false,
        flex: 1,
      },
      {
        headerName: t('common.terms.products'),
        field: 'children',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<IProductDto, IProductDto['children']>) => {
          const { value } = params;
          return <ProductsTooltip products={value ?? []} />;
        },
      },
      {
        headerName: t('common.terms.actions'),
        field: 'id',
        width: 260,
        sortable: false,
        renderCell: (params: GridRenderCellParams<IProductDto, IProductDto['id']>) => {
          const { value: id, row } = params;
          const isCustomize = id === CUSTOMIZE_PACKAGE_ID;
          return (
            <Button
              variant="contained"
              size="medium"
              endIcon={<ArrowRightIcon />}
              onClick={() => handleChosePackageProduct(row)}
            >
              {isCustomize
                ? t('orders.pages.new.stepOne.buildYourOwnPackageAction')
                : t('common.actions.choosePackage')}
            </Button>
          );
        },
      },
    ];
    if (user?.visiblePrices)
      columns.splice(2, 0, {
        headerName: t('common.terms.price'),
        field: 'discountedPrice',
        width: 250,
        sortable: false,
        renderCell: (params: GridRenderCellParams<IProductDto, IProductDto['discountedPrice']>) => {
          const { value } = params;
          return value ? f(value || 0, { currency: CurrencyCode.DKK, decimals: 2 }) : '-';
        },
      });
    return columns;
  }, [t, f, user?.visiblePrices]);

  return {
    ui: {
      table: {
        columns: packagesColumns,
      },
    },
  };
};
